import { AfterViewInit, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav/sidenav';
import { Subscription } from 'rxjs';

const HOVER_DELAY = 300;

@Component({
  selector: 'app-sidenav-toggle',
  templateUrl: './sidenav-toggle.component.html',
  styleUrls: ['./sidenav-toggle.component.scss']
})
export class SidenavToggleComponent implements AfterViewInit, OnDestroy {
  @Input() sidenav: MatSidenav;

  showToggle = false;
  gutterHovered = false;

  private fixedToggle: HTMLElement | null = null;
  private gutterVisual: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private hoverTimer: any = null;
  private gutterTimer: any = null;
  private resizeObserver: ResizeObserver | null = null;
  private openedChangeSub: Subscription | null = null;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;
    this.fixedToggle = host.querySelector<HTMLElement>('.sidenav-fixed-toggle');
    this.gutterVisual = host.querySelector<HTMLElement>('.sidenav-gutter-visual');
    this.container = host.closest<HTMLElement>('mat-sidenav-container');

    if (this.container) {
      if (this.fixedToggle) {
        this.container.appendChild(this.fixedToggle);
      }
      if (this.gutterVisual) {
        this.container.appendChild(this.gutterVisual);
        this.updateGutterVisualPosition();
        this.resizeObserver = new ResizeObserver(() => this.updateGutterVisualPosition());
        this.resizeObserver.observe(this.container);
        this.openedChangeSub = this.sidenav?.openedChange.subscribe(() => this.updateGutterVisualPosition());
      }
    }
  }

  private updateGutterVisualPosition(): void {
    if (!this.gutterVisual || !this.container) return;
    const sidenavEl = this.container.querySelector<HTMLElement>('mat-sidenav');
    if (!sidenavEl) return;
    const containerRect = this.container.getBoundingClientRect();
    const sidenavRect = sidenavEl.getBoundingClientRect();
    this.gutterVisual.style.left = `${sidenavRect.right - containerRect.left}px`;
  }

  onStripEnter(): void {
    this.hoverTimer = setTimeout(() => this.showToggle = true, HOVER_DELAY);
  }

  onVisualEnter(): void {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
    this.showToggle = true;
  }

  onStripLeave(): void {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
    this.showToggle = false;
  }

  onGutterEnter(): void {
    clearTimeout(this.gutterTimer);
    this.gutterTimer = setTimeout(() => this.gutterHovered = true, HOVER_DELAY);
  }

  onGutterVisualEnter(): void {
    clearTimeout(this.gutterTimer);
    this.gutterTimer = null;
    this.gutterHovered = true;
  }

  onGutterLeave(): void {
    clearTimeout(this.gutterTimer);
    this.gutterTimer = setTimeout(() => {
      this.gutterHovered = false;
      this.gutterTimer = null;
    }, 80);
  }

  toggleSidenav(): void {
    this.sidenav?.toggle();
  }

  onGutterClick(): void {
    clearTimeout(this.gutterTimer);
    this.gutterTimer = null;
    this.gutterHovered = false;
    this.toggleSidenav();
  }

  ngOnDestroy(): void {
    clearTimeout(this.hoverTimer);
    clearTimeout(this.gutterTimer);
    this.resizeObserver?.disconnect();
    this.openedChangeSub?.unsubscribe();
    if (this.fixedToggle?.parentElement) {
      this.fixedToggle.parentElement.removeChild(this.fixedToggle);
    }
    if (this.gutterVisual?.parentElement) {
      this.gutterVisual.parentElement.removeChild(this.gutterVisual);
    }
  }
}
